import { useEffect } from 'react';
import { useAuth } from '../features/auth/AuthProvider';
import { useTheme } from './theme';

/**
 * Ties the account's theme setting to the one this page is painting with.
 *
 * It is a component rather than a call inside either provider because of where
 * they sit: `ThemeProvider` wraps `AuthProvider` (the theme has to apply on the
 * sign-in screen, before there is a user), so the theme provider cannot read
 * `useAuth`. A leaf rendered inside both can read both, and neither provider
 * has to know the other exists.
 *
 * Renders nothing.
 */
export function ThemeSync() {
  const { user, loading } = useAuth();
  const { adoptServerPreference } = useTheme();

  useEffect(() => {
    // While `/me` is still in flight there is no answer yet, and treating
    // that as "signed out" would stop a change made in the first second from
    // reaching the account.
    if (loading) return;
    if (!user) {
      adoptServerPreference(null);
      return;
    }
    // The id travels with the theme because the provider needs both: the
    // theme to paint, and WHO it belongs to, so that a write can be bound to
    // that account's session and a read for a different account can be told
    // apart from a stale read for this one.
    //
    // A `/me` from before the column existed carries no theme. Absent means
    // never chosen, which is what 'system' means; the provider applies that
    // default rather than this effect guessing at it.
    adoptServerPreference({ id: user.id, theme: user.theme });
  }, [adoptServerPreference, loading, user]);

  return null;
}
